import { useNavigate } from 'react-router-dom';
import {
  RiPhoneFill, RiChat1Line,
  RiSubtractLine, RiExpandDiagonalLine, RiWhatsappLine,
  RiMicFill, RiMicOffFill, RiVolumeUpFill, RiVolumeMuteFill,
  RiLockFill, RiArrowRightUpLine, RiArrowLeftDownLine,
  RiDeleteBin5Line, RiGridFill
} from 'react-icons/ri';
import { useCall } from '../context/CallContext';
import './WhatsAppCallModal.css';

const KEYPAD_KEYS = [
  { k: '1', sub: '' }, { k: '2', sub: 'ABC' }, { k: '3', sub: 'DEF' },
  { k: '4', sub: 'GHI' }, { k: '5', sub: 'JKL' }, { k: '6', sub: 'MNO' },
  { k: '7', sub: 'PQRS' }, { k: '8', sub: 'TUV' }, { k: '9', sub: 'WXYZ' },
  { k: '*', sub: '' }, { k: '0', sub: '+' }, { k: '#', sub: '' },
];

const formatDuration = (sec = 0) => {
  const total = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function WhatsAppCallModal() {
  const {
    activeCall,
    isMuted,
    isSpeaker,
    isKeypadOpen,
    minimized,
    dtmfDigits,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    toggleKeypad,
    setMinimized,
    handleKeypadPress,
    setDtmfDigits,
  } = useCall();

  const navigate = useNavigate();

  if (!activeCall) return null;

  const isIncoming = activeCall.direction === 'inbound';
  const isRinging = activeCall.status === 'ringing';
  const isCalling = activeCall.status === 'calling';
  const isConnected = activeCall.status === 'connected';
  const isEnded = activeCall.status === 'ended';

  const cleanPhone = String(activeCall.phone || '').replace(/\D/g, '');
  const formattedPhone = cleanPhone ? `+${cleanPhone}` : '';
  const callerName = activeCall.customerName && activeCall.customerName !== cleanPhone
    ? activeCall.customerName
    : (formattedPhone || 'Unknown Caller');
  const initial = callerName.charAt(0).toUpperCase() || '?';

  const handleOpenChat = () => {
    setMinimized(true);
    navigate('/inbox', { state: { openPhone: `${cleanPhone}@s.whatsapp.net` } });
  };

  // ── 1. PICTURE-IN-PICTURE MINIMIZED FLOATING PILL ───────────────────────
  if (minimized) {
    return (
      <div
        className="wac-minimized-pill"
        role="button"
        tabIndex={0}
        onClick={() => setMinimized(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setMinimized(false); }}
      >
        <div className="wac-mini-avatar">
          <RiWhatsappLine size={18} color="#25d366" />
        </div>
        <div className="wac-mini-info">
          <span className="wac-mini-name">{callerName}</span>
          <span className="wac-mini-status">
            {isConnected ? (
              <span className="wac-mini-timer">🟢 {formatDuration(activeCall.duration)}</span>
            ) : isEnded ? (
              <span className="wac-mini-ended">Call Ended</span>
            ) : isRinging ? (
              'Ringing...'
            ) : (
              'Calling...'
            )}
          </span>
        </div>

        <div className="wac-mini-actions" onClick={e => e.stopPropagation()}>
          {isConnected && (
            <button
              type="button"
              className={`wac-mini-btn ${isMuted ? 'muted' : ''}`}
              title={isMuted ? 'Unmute' : 'Mute'}
              onClick={toggleMute}
            >
              {isMuted ? <RiMicOffFill size={14} /> : <RiMicFill size={14} />}
            </button>
          )}
          <button
            type="button"
            className="wac-mini-btn hangup"
            title="End Call"
            onClick={endCall}
          >
            <RiPhoneFill size={14} />
          </button>
          <button
            type="button"
            className="wac-mini-btn expand"
            title="Expand Call Screen"
            onClick={() => setMinimized(false)}
          >
            <RiExpandDiagonalLine size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ── 2. FULL SCREEN-CENTERED CALL MODAL ──────────────────────────────────
  return (
    <div className="wac-backdrop">
      <div
        className="wac-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wac-caller-name"
      >
        {/* Top Bar */}
        <div className="wac-topbar">
          <div className="wac-badge">
            <RiWhatsappLine size={16} color="#25d366" />
            <span>WhatsApp Voice Call</span>
          </div>

          <div className="wac-encryption-badge" title="End-to-end encrypted audio stream">
            <RiLockFill size={11} />
            <span>End-to-end encrypted</span>
          </div>

          <button
            type="button"
            className="wac-minimize-btn"
            title="Minimize to floating pill"
            onClick={() => setMinimized(true)}
          >
            <RiSubtractLine size={18} />
          </button>
        </div>

        {/* Caller Avatar with Ripple Waves */}
        <div className="wac-avatar-wrap">
          {(isCalling || isRinging) && (
            <>
              <span className="wac-pulse-ring ring-1" />
              <span className="wac-pulse-ring ring-2" />
              <span className="wac-pulse-ring ring-3" />
            </>
          )}

          <div className={`wac-avatar-circle ${isConnected ? 'connected' : ''} ${isEnded ? 'ended' : ''}`}>
            <span className="wac-avatar-initial">
              {initial}
            </span>
          </div>
        </div>

        {/* Caller Details */}
        <div className="wac-caller-info">
          <h2 id="wac-caller-name" className="wac-caller-name">{callerName}</h2>
          <div className="wac-caller-sub">
            {formattedPhone && <span className="wac-phone-number">{formattedPhone}</span>}
            <span className="wac-direction-tag">
              {isIncoming ? (
                <><RiArrowLeftDownLine size={13} color="#22c55e" /> Incoming WhatsApp Call</>
              ) : (
                <><RiArrowRightUpLine size={13} color="#3b82f6" /> Outbound WhatsApp Call</>
              )}
            </span>
          </div>

          {/* Status Indicator / Live Stopwatch */}
          <div className="wac-status-banner">
            {isCalling && (
              <div className="wac-state-calling">
                <span>Calling</span>
                <span className="wac-dots-loader">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}

            {isRinging && (
              <div className="wac-state-ringing">
                <span className="wac-ringing-bell">🔔</span>
                <span>Ringing...</span>
              </div>
            )}

            {isConnected && (
              <div className="wac-state-connected">
                <span className="wac-live-dot" />
                <span className="wac-duration-clock">{formatDuration(activeCall.duration)}</span>
                <div className="wac-equalizer-bars">
                  <span className="eq-bar bar-1" />
                  <span className="eq-bar bar-2" />
                  <span className="eq-bar bar-3" />
                  <span className="eq-bar bar-4" />
                  <span className="eq-bar bar-5" />
                </div>
              </div>
            )}

            {isEnded && (
              <div className="wac-state-ended">
                <span>Call Ended</span>
              </div>
            )}
          </div>
        </div>

        {/* In-Call Keypad Drawer */}
        {isKeypadOpen && (
          <div className="wac-keypad-drawer">
            <div className="wac-keypad-display">
              <span>{dtmfDigits || 'Press digits...'}</span>
              {dtmfDigits && (
                <button
                  type="button"
                  className="wac-keypad-clear"
                  onClick={() => setDtmfDigits('')}
                  title="Clear digits"
                >
                  <RiDeleteBin5Line size={14} />
                </button>
              )}
            </div>
            <div className="wac-keypad-grid">
              {KEYPAD_KEYS.map(({ k, sub }) => (
                <button
                  type="button"
                  key={k}
                  className="wac-kp-btn"
                  onClick={() => handleKeypadPress(k)}
                >
                  <span className="kp-num">{k}</span>
                  {sub && <span className="kp-sub">{sub}</span>}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="wac-keypad-close"
              onClick={toggleKeypad}
            >
              Hide Keypad
            </button>
          </div>
        )}

        {/* Bottom Call Controls Bar (WhatsApp Calling Options) */}
        <div className="wac-controls-bar">
          {/* If Incoming and still ringing: Accept vs Decline */}
          {isIncoming && isRinging ? (
            <div className="wac-incoming-actions">
              <button
                type="button"
                className="wac-action-circle decline"
                onClick={declineCall}
                title="Decline Call"
              >
                <div className="wac-icon-inner hangup-rot">
                  <RiPhoneFill size={26} />
                </div>
                <span className="wac-btn-lbl">Decline</span>
              </button>

              <button
                type="button"
                className="wac-action-circle accept"
                onClick={acceptCall}
                title="Accept Call"
              >
                <div className="wac-icon-inner">
                  <RiPhoneFill size={28} />
                </div>
                <span className="wac-btn-lbl">Accept</span>
              </button>
            </div>
          ) : (
            /* Active / Connected / Outbound Call Controls Bar */
            <div className="wac-active-actions">
              {/* Mute Button */}
              <button
                type="button"
                className={`wac-ctrl-btn ${isMuted ? 'active-alert' : ''}`}
                onClick={toggleMute}
                title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              >
                <div className="wac-ctrl-circle">
                  {isMuted ? <RiMicOffFill size={22} /> : <RiMicFill size={22} />}
                </div>
                <span className="wac-ctrl-label">{isMuted ? 'Muted' : 'Mute'}</span>
              </button>

              {/* Speaker Button */}
              <button
                type="button"
                className={`wac-ctrl-btn ${isSpeaker ? 'active-speaker' : ''}`}
                onClick={toggleSpeaker}
                title="Toggle Speaker / Earpiece"
              >
                <div className="wac-ctrl-circle">
                  {isSpeaker ? <RiVolumeUpFill size={22} /> : <RiVolumeMuteFill size={22} />}
                </div>
                <span className="wac-ctrl-label">{isSpeaker ? 'Speaker' : 'Earpiece'}</span>
              </button>

              {/* Keypad Button */}
              <button
                type="button"
                className={`wac-ctrl-btn ${isKeypadOpen ? 'active-keypad' : ''}`}
                onClick={toggleKeypad}
                title="In-call keypad dialer"
              >
                <div className="wac-ctrl-circle">
                  <RiGridFill size={22} />
                </div>
                <span className="wac-ctrl-label">Keypad</span>
              </button>

              {/* Chat in Inbox Button */}
              <button
                type="button"
                className="wac-ctrl-btn"
                onClick={handleOpenChat}
                title="Open Chat in WhatsApp Inbox"
              >
                <div className="wac-ctrl-circle">
                  <RiChat1Line size={22} />
                </div>
                <span className="wac-ctrl-label">Chat</span>
              </button>

              {/* End Call / Cut Button (Red WhatsApp Hangup) */}
              <button
                type="button"
                className="wac-ctrl-btn end-call"
                onClick={endCall}
                title="End WhatsApp Call"
                disabled={isEnded}
              >
                <div className="wac-ctrl-circle hangup-circle">
                  <RiPhoneFill size={28} className="hangup-rot" />
                </div>
                <span className="wac-ctrl-label hangup-text">End Call</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

